import { useSettingsStore } from "@/data/settingsStore";

export interface AccreditationRequest {
  id: string;
  userId: string;
  userName: string;
  email: string;
  partyAcronym: string;
  documentUrl?: string; // Simule l'upload d'un justificatif
  status: 'pending' | 'approved' | 'rejected' | 'blocked';
  emailCode?: string; // Simule le code généré envoyé par email
  createdAt: string;
  // Extra Info
  role?: string;
  phone?: string;
  qualite?: string;
  categorieSociopro?: string;
  genre?: string;
  age?: string;
  residence?: string;
  affinitePolitique?: string;
  metadata?: string;
}

const STORAGE_KEY = "kaynin_accreditations";
const STRICT_MODE_KEY = "kaynin_strict_accreditation_mode";

import { getApiUrl } from "@/utils/apiConfig";

const API_URL = getApiUrl();

export const getStrictAccreditationMode = (): boolean => {
  const data = localStorage.getItem(STRICT_MODE_KEY);
  return data ? JSON.parse(data) : true;
};

export function setStrictAccreditationMode(enabled: boolean) {
  useSettingsStore.getState().updateSetting("kaynin_strict_accreditation_mode", enabled);
  window.dispatchEvent(new Event("accreditationsSettingsChanged"));
};

let cachedAccreditations: AccreditationRequest[] = [];
let isAccreditationsLoaded = false;

export const getAccreditations = (): AccreditationRequest[] => {
  if (!isAccreditationsLoaded) {
    isAccreditationsLoaded = true;
    fetch(`${API_URL}/accreditations.php`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          cachedAccreditations = data;
          window.dispatchEvent(new Event("accreditationsChanged"));
        }
      })
      .catch(err => console.error("Error fetching accreditations:", err));
  }
  return cachedAccreditations;
};

const saveAccreditations = (data: AccreditationRequest[]) => {
  // Only updates local cache for fast UI response
  cachedAccreditations = data;
  window.dispatchEvent(new Event("accreditationsChanged"));
};

export const refreshAccreditations = (): void => {
  isAccreditationsLoaded = false;
  getAccreditations();
};

/**
 * Le militant demande une accréditation avec son document.
 * Un code secret à 6 chiffres est généré "et envoyé par mail".
 */
export const requestAccreditation = (
  userId: string,
  userName: string,
  email: string,
  partyAcronym: string,
  documentUrl: string,
  extraInfo?: { role?: string, phone?: string, qualite?: string }
): AccreditationRequest => {
  const requests = getAccreditations();
  
  // Find existing to preserve extraInfo if not provided
  const existingReq = requests.find(r => r.userId === userId || r.email === email);
  
  // Clean up previous requests for this user
  const filtered = requests.filter(r => r.userId !== userId && r.email !== email);
  
  const code = Math.floor(100000 + Math.random() * 900000).toString(); // Code à 6 chiffres
  
  const newRequest: AccreditationRequest = {
    id: Math.random().toString(36).substr(2, 9),
    userId,
    userName,
    email,
    partyAcronym,
    documentUrl,
    status: 'pending',
    emailCode: code,
    createdAt: new Date().toISOString(),
    role: extraInfo?.role || existingReq?.role,
    phone: extraInfo?.phone || existingReq?.phone,
    qualite: extraInfo?.qualite || existingReq?.qualite
  };
  
  saveAccreditations([newRequest, ...filtered]);
  
  fetch(`${API_URL}/accreditations.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'request', ...newRequest })
  });

  // "Simulation" de l'envoi d'email
  console.log(`[EMAIL SIMULATION] Envoyé à ${email} : Votre code d'activation Délégué ${partyAcronym} est : ${code}`);
  
  return newRequest;
};

/**
 * Le militant active son statut en saisissant le code reçu par mail.
 */
export const activateWithCode = (userId: string, code: string): boolean => {
  const requests = getAccreditations();
  const reqIndex = requests.findIndex(r => r.userId === userId && r.status === 'pending');
  
  if (reqIndex > -1 && requests[reqIndex].emailCode === code) {
    requests[reqIndex].status = 'approved';
    saveAccreditations(requests);
    fetch(`${API_URL}/accreditations.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'activate', userId, code })
    });
    return true; // Succès
  }
  return false;
};

/**
 * L'Admin Kayn'in active manuellement un délégué.
 */
export const adminApproveRequest = (requestId: string): void => {
  const requests = getAccreditations();
  const req = requests.find(r => r.id === requestId);
  if (req) {
    req.status = 'approved';
    saveAccreditations(requests);
    fetch(`${API_URL}/accreditations.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'status', id: requestId, status: 'approved' }) });
  }
};

export const adminRejectRequest = (requestId: string): void => {
  const requests = getAccreditations();
  const req = requests.find(r => r.id === requestId);
  if (req) {
    req.status = 'rejected';
    saveAccreditations(requests);
    fetch(`${API_URL}/accreditations.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'status', id: requestId, status: 'rejected' }) });
  }
};

export const adminBlockRequest = (requestId: string): void => {
  const requests = getAccreditations();
  const req = requests.find(r => r.id === requestId);
  if (req) {
    req.status = 'blocked';
    saveAccreditations(requests);
    fetch(`${API_URL}/accreditations.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'status', id: requestId, status: 'blocked' }) });
  }
};

export const adminDeleteRequest = (requestId: string): void => {
  const requests = getAccreditations();
  const filtered = requests.filter(r => r.id !== requestId);
  saveAccreditations(filtered);
  fetch(`${API_URL}/accreditations.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'delete', id: requestId }) });
};

export const getUserAccreditation = (userId: string): AccreditationRequest | undefined => {
  return getAccreditations().find(r => r.userId === userId);
};

export const getPendingAccreditations = (): AccreditationRequest[] => {
  return getAccreditations().filter(r => r.status === 'pending');
};
